import React, { useContext, useEffect } from "react";
import { Grid, TextField } from "@material-ui/core";
import {
  DatePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import { AuthContext } from "context/context";
import { EndosoContext } from "pages/endoso/context";
import { endoso } from "services";
import { formatDate } from "pages/endoso/hooks";
import { useStyles } from "./styles";

const parseHora = (hora) =>
  hora ? new Date(`1970-01-01T${hora}:00`) : null;

// Lugar/Ubicación: solo mayúsculas, números y espacios. Convierte minúsculas
// y acentos (MÉXICO -> MEXICO) y conserva la Ñ; elimina cualquier otro carácter.
const saneaTexto24 = (valor) =>
  String(valor ?? "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/([AEIOU])[̀-ͯ]+/g, "$1")
    .normalize("NFC")
    .replace(/[^A-ZÑ0-9 ]/g, "");

const isSameDay = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  return (
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()
  );
};

export const NuevoTextoForm = () => {
  const classes = useStyles();
  const { state } = useContext(AuthContext);
  const {
    textoSalidaAlumnos24: formData,
    setTextoSalidaAlumnos24: setFormData,
    textoCargado24,
    setTextoCargado24,
  } = useContext(EndosoContext);

  useEffect(() => {
    if (textoCargado24 || !state) return; // sin state todavía no hay oficina/ramo/poliza que consultar
    let cancelled = false;
    const getTextosGuardados = async () => {
      const res = await endoso.getTextoSalidaAlumnos({
        oficina: state.oficina,
        idramo: state.ramo,
        poliza: state.poliza,
      });
      if (cancelled) return; // el form se desmontó (ciclo de spinner): no pisar ediciones
      if (res?.data?.codigo === 1) {
        const datos = res.data;
        setFormData({
          lugar: saneaTexto24(datos.lugSalAlumnos),
          ubicacion: saneaTexto24(datos.ubicSalAlumnos),
          salida: datos.fecSalAlumnos
            ? new Date(`${datos.fecSalAlumnos}T00:00:00`)
            : null,
          horaSalida: parseHora(datos.horSalAlumnos),
          regreso: datos.fecRegAlumnos
            ? new Date(`${datos.fecRegAlumnos}T00:00:00`)
            : null,
          horaRegreso: parseHora(datos.horRegAlumnos),
          numeroAsegurados: datos.asegSalAlumnos || "",
        });
      }
      setTextoCargado24(true);
    };
    getTextosGuardados();
    return () => {
      cancelled = true;
    };
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeTexto = (field) => (e) => {
    const value = saneaTexto24(e?.target?.value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const vigenciaMinDate = state?.iniVigencia
    ? new Date(formatDate(state.iniVigencia))
    : undefined;
  const minDate =
    vigenciaMinDate && vigenciaMinDate > today ? vigenciaMinDate : today;
  const maxDate = state?.finVigencia
    ? new Date(formatDate(state.finVigencia))
    : undefined;
  const sameDayTrip = isSameDay(formData.salida, formData.regreso);

  return (
    <MuiPickersUtilsProvider utils={ DateFnsUtils } locale={ esLocale }>
      <Grid container spacing={ 2 } className={ classes.container }>

        <Grid item xs={ 12 } sm={ 6 }>
          <div className={ classes.label }>Lugar (*)</div>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={ formData.lugar }
            onChange={ handleChangeTexto("lugar") }
            inputProps={ { maxLength: 40 } }
          />
        </Grid>

        <Grid item xs={ 12 } sm={ 6 }>
          <div className={ classes.label }>Ubicación (*)</div>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={ formData.ubicacion }
            onChange={ handleChangeTexto("ubicacion") }
            inputProps={ { maxLength: 40 } }
          />
        </Grid>

        <Grid item xs={ 12 } sm={ 3 }>
          <div className={ classes.label }>Salida (*)</div>
          <DatePicker
            fullWidth
            inputVariant="outlined"
            size="small"
            format="dd/MM/yyyy"
            value={ formData.salida }
            onChange={ handleChange("salida") }
            minDate={ minDate }
            maxDate={ maxDate }
            invalidDateMessage="Fecha inválida"
            minDateMessage="la fecha seleccionada no se encuentra dentro de la vigencia de la póliza"
            maxDateMessage="la fecha seleccionada no se encuentra dentro de la vigencia de la póliza"
          />
        </Grid>

        <Grid item xs={ 12 } sm={ 3 }>
          <div className={ classes.label }>Hora de salida (*)</div>
          <KeyboardTimePicker
            fullWidth
            inputVariant="outlined"
            size="small"
            ampm={ false }
            format="HH:mm"
            placeholder="HH:MM"
            value={ formData.horaSalida }
            onChange={ handleChange("horaSalida") }
            invalidDateMessage="La hora debe tener formato HH:MM"
          />
        </Grid>

        <Grid item xs={ 12 } sm={ 3 }>
          <div className={ classes.label }>Regreso (*)</div>
          <DatePicker
            fullWidth
            inputVariant="outlined"
            size="small"
            format="dd/MM/yyyy"
            value={ formData.regreso }
            onChange={ handleChange("regreso") }
            minDate={ formData.salida || minDate }
            maxDate={ maxDate }
            invalidDateMessage="Fecha inválida"
            minDateMessage="la fecha seleccionada no se encuentra dentro de la vigencia de la póliza"
            maxDateMessage="la fecha seleccionada no se encuentra dentro de la vigencia de la póliza"
          />
        </Grid>

        <Grid item xs={ 12 } sm={ 3 }>
          <div className={ classes.label }>Hora de regreso (*)</div>
          <KeyboardTimePicker
            fullWidth
            inputVariant="outlined"
            size="small"
            ampm={ false }
            format="HH:mm"
            placeholder="HH:MM"
            value={ formData.horaRegreso }
            onChange={ handleChange("horaRegreso") }
            invalidDateMessage="La hora debe tener formato HH:MM"
            minTime={ sameDayTrip ? formData.horaSalida : undefined }
            minTimeMessage="La hora de regreso no puede ser anterior a la hora de salida."
          />
        </Grid>

        <Grid item xs={ 12 } sm={ 4 }>
          <div className={ classes.label }>Número de asegurados (*)</div>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            value={ formData.numeroAsegurados }
            onChange={ handleChange("numeroAsegurados") }
            onInput={ (e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, "");
            } }
            inputProps={ { min: 1 } }
          />
        </Grid>

      </Grid>
    </MuiPickersUtilsProvider>
  );
};
